/*!
 * 首次登入強制修改密碼（v2.32.0）
 *
 * 帳號由管理者建立，初始密碼是系統產生的隨機字串、經由私訊傳遞。
 * 那串東西會留在對話記錄裡，所以一定要換掉才算真的「只有他自己知道」。
 *
 * ⚠️ **這個畫面不是安全機制，只是入口。** 真正的強制在後端
 * `Backend/jwt_protection.py`：`must_change_password` 為 true 時，
 * 除了 change-password / getUser / logout 之外每一支 API 都回 403，
 * 所以就算有人繞過這個畫面、拿 token 直接打 API 一樣沒用。
 */
import React, { useState } from "react";
import {
  Alert, Button, Card, CardBody, Col, Container, Form, FormGroup,
  Input, Label, Row,
} from "reactstrap";
import axios from "axios";

import JWTManager from "constant/JWTManager";
import { COLORS, inputStyle, labelStyle, saveBtnStyle } from "constant/adminTheme";
// 規則與右上角的「修改密碼」Modal 共用，避免兩份實作漂掉
import { MIN_LENGTH, validateNewPassword } from "utils/passwordPolicy";

export default function ForcePasswordChange({ username, onDone }) {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const tooShort = newPassword.length > 0 && newPassword.length < MIN_LENGTH;
  const mismatch = confirm.length > 0 && newPassword !== confirm;
  const sameAsOld = newPassword.length > 0 && newPassword === oldPassword;
  const canSubmit = !saving && !validateNewPassword(oldPassword, newPassword, confirm);

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      const res = await axios.post("/api/change-password", {
        old_password: oldPassword,
        new_password: newPassword,
      });
      if (res.data.code === 1) {
        // 密碼換了，但手上這張 token 的 identity 還記著 must_change_password=true。
        // 最乾淨的做法是重登 —— 也順便驗證新密碼真的能用。
        JWTManager.clearToken();
        if (onDone) onDone();
        window.location.href = "/login";
      } else {
        setError(res.data.msg || "修改失敗");
      }
    } catch (err) {
      setError(err.response?.data?.msg || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#1a1f2b", display: "flex", alignItems: "center" }}>
      <Container>
        <Row className="justify-content-center">
          <Col md="6" lg="5">
            <Card style={{ background: COLORS.cardBg, border: `1px solid ${COLORS.border}` }}>
              <CardBody className="p-4">
                <div className="text-center mb-4">
                  <i className="fa fa-key" style={{ fontSize: 40, color: COLORS.warning }} />
                  <h4 style={{ color: COLORS.textPrimary, marginTop: 14, marginBottom: 6 }}>
                    請設定你自己的密碼
                  </h4>
                  <p style={{ color: COLORS.textMuted, fontSize: 13, marginBottom: 0 }}>
                    <code style={{ color: COLORS.accent }}>{username}</code> 目前用的是系統發的初始密碼。
                    改完之後就<strong>只有你自己知道</strong>。
                  </p>
                </div>

                {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

                <Form onSubmit={submit}>
                  <FormGroup>
                    <Label style={labelStyle}>目前的密碼（管理者給你的那串）</Label>
                    <Input type="password" style={inputStyle} value={oldPassword} autoFocus
                      autoComplete="current-password"
                      onChange={(e) => setOldPassword(e.target.value)} />
                  </FormGroup>
                  <FormGroup>
                    <Label style={labelStyle}>新密碼</Label>
                    <Input type="password" style={inputStyle} value={newPassword}
                      autoComplete="new-password"
                      onChange={(e) => setNewPassword(e.target.value)} />
                    <small style={{ color: tooShort ? COLORS.danger : COLORS.textMuted }}>
                      至少 {MIN_LENGTH} 個字元
                      {sameAsOld && <span style={{ color: COLORS.danger }}>；不能跟目前的密碼相同</span>}
                    </small>
                  </FormGroup>
                  <FormGroup>
                    <Label style={labelStyle}>再輸入一次</Label>
                    <Input type="password" style={inputStyle} value={confirm}
                      autoComplete="new-password"
                      onChange={(e) => setConfirm(e.target.value)} />
                    {mismatch && (
                      <small style={{ color: COLORS.danger }}>兩次輸入不一致</small>
                    )}
                  </FormGroup>
                  <Button block type="submit" style={saveBtnStyle(!canSubmit)} disabled={!canSubmit}>
                    {saving ? "更新中..." : "設定新密碼"}
                  </Button>
                </Form>

                <p style={{ color: COLORS.textDimmed, fontSize: 12, marginTop: 18, marginBottom: 0 }}>
                  <i className="fa fa-info-circle mr-1" />
                  改完會回到登入頁，請用新密碼登入一次。
                  忘記密碼要請管理者重設，系統查不回舊的。
                </p>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
